// shared-components/hooks/useGradient.js
import { useMemo } from "react";
import { useTheme } from "@shared/lib/theme";
import { GRADIENTS } from "@shared/constants/designSystem";

/**
 * Custom hook for accessing gradient classes based on theme and special numbers
 */
export function useGradient() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Memoize the gradients to avoid recreation on each render
  const gradients = useMemo(() => {
    // Base gradients from the original system
    const baseGradients = {
      // Standard gradients
      primary:
        "bg-gradient-to-r from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800",
      secondary:
        "bg-gradient-to-r from-secondary-500 to-secondary-700 dark:from-secondary-600 dark:to-secondary-800",
      accent:
        "bg-gradient-to-r from-accent-500 to-accent-700 dark:from-accent-600 dark:to-accent-800",

      // Three-color smooth gradients
      primarySmooth:
        "bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 dark:from-primary-600 dark:via-primary-700 dark:to-primary-800",
      secondarySmooth:
        "bg-gradient-to-r from-secondary-500 via-secondary-600 to-secondary-700 dark:from-secondary-600 dark:via-secondary-700 dark:to-secondary-800",
      accentSmooth:
        "bg-gradient-to-r from-accent-500 via-accent-600 to-accent-700 dark:from-accent-600 dark:via-accent-700 dark:to-accent-800",

      // Special angle gradients
      special108:
        "bg-gradient-108 from-primary-500 to-accent-500 dark:from-primary-600 dark:to-accent-600",
      special3:
        "bg-gradient-3 from-primary-500 to-secondary-500 dark:from-primary-600 dark:to-secondary-600",
      golden:
        "bg-gradient-golden from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800",

      // Radial gradients
      radial:
        "bg-radial from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800",
      radialGolden:
        "bg-radial-golden from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800",

      // Conic gradient
      conicGolden:
        "bg-conic-golden from-primary-500 via-secondary-500 to-accent-500",
    };

    // Extended gradients for more versatility and specific applications
    const extendedGradients = {
      // Layout-specific subtle gradients
      layoutHeader: isDark
        ? "bg-gradient-108 from-primary-700 via-primary-900 to-primary-800"
        : "bg-gradient-108 from-primary-300 via-primary-100 to-primary-200",

      layoutFooter: isDark
        ? "bg-gradient-108 from-secondary-800 via-secondary-900 to-secondary-700"
        : "bg-gradient-108 from-secondary-300 via-secondary-100 to-secondary-200",

      layoutMain: isDark
        ? "bg-gradient-golden from-primary-700 via-accent-700 to-secondary-700"
        : "bg-gradient-golden from-primary-300 via-accent-100 to-secondary-200",

      layoutSidebar: isDark
        ? "bg-gradient-108 from-primary-800 to-primary-900"
        : "bg-gradient-108 from-primary-50 to-white",

      // Section gradients
      sectionLight: isDark
        ? "bg-gradient-to-r from-primary-800/30 to-primary-900/30"
        : "bg-gradient-to-r from-primary-50/60 to-primary-100/60",

      sectionMedium: isDark
        ? "bg-gradient-to-r from-primary-700/70 to-primary-800/70"
        : "bg-gradient-to-r from-primary-100 to-primary-200",

      sectionBold: isDark
        ? "bg-gradient-to-r from-primary-600 to-primary-800"
        : "bg-gradient-to-r from-primary-300 to-primary-500",

      // Component-specific gradients
      cardLight: isDark
        ? "bg-gradient-to-br from-gray-800 to-gray-900"
        : "bg-gradient-to-br from-white to-gray-100",

      cardMedium: isDark
        ? "bg-gradient-to-br from-primary-800/70 to-primary-900/70"
        : "bg-gradient-to-br from-primary-50 to-primary-100",

      cardBold: isDark
        ? "bg-gradient-to-br from-primary-600 to-primary-800"
        : "bg-gradient-to-br from-primary-300 to-primary-500",

      // Multi-color gradients with different angles
      primarySecondary108:
        "bg-gradient-108 from-primary-500 to-secondary-500 dark:from-primary-600 dark:to-secondary-600",
      primaryAccent108:
        "bg-gradient-108 from-primary-500 to-accent-500 dark:from-primary-600 dark:to-accent-600",
      secondaryAccent108:
        "bg-gradient-108 from-secondary-500 to-accent-500 dark:from-secondary-600 dark:to-accent-600",

      primarySecondary3:
        "bg-gradient-3 from-primary-500 to-secondary-500 dark:from-primary-600 dark:to-secondary-600",
      primaryAccent3:
        "bg-gradient-3 from-primary-500 to-accent-500 dark:from-primary-600 dark:to-accent-600",
      secondaryAccent3:
        "bg-gradient-3 from-secondary-500 to-accent-500 dark:from-secondary-600 dark:to-accent-600",

      primarySecondaryGolden:
        "bg-gradient-golden from-primary-500 to-secondary-500 dark:from-primary-600 dark:to-secondary-600",
      primaryAccentGolden:
        "bg-gradient-golden from-primary-500 to-accent-500 dark:from-primary-600 dark:to-accent-600",
      secondaryAccentGolden:
        "bg-gradient-golden from-secondary-500 to-accent-500 dark:from-secondary-600 dark:to-accent-600",

      // New custom gradients for Graham's blue theme
      blueLayered: isDark
        ? "bg-gradient-to-r from-primary-800 via-primary-900 to-primary-800"
        : "bg-gradient-to-r from-primary-200 via-primary-100 to-primary-200",

      blueGreen: isDark
        ? "bg-gradient-to-br from-primary-700 to-accent-700"
        : "bg-gradient-to-br from-primary-400 to-accent-400",

      oceanDepth: isDark
        ? "bg-gradient-to-b from-primary-900 via-primary-800 to-primary-900"
        : "bg-gradient-to-b from-primary-100 via-white to-primary-100",

      skyToSea: isDark
        ? "bg-gradient-108 from-blue-800 to-teal-800"
        : "bg-gradient-108 from-blue-400 to-teal-400",
    };

    // Intensity variations for key gradients
    const intensityVariations = {
      // Primary variations
      primaryLight: isDark
        ? "bg-gradient-to-r from-primary-800/30 to-primary-700/30"
        : "bg-gradient-to-r from-primary-100 to-primary-50",

      primaryMedium:
        "bg-gradient-to-r from-primary-600 to-primary-500 dark:from-primary-700 dark:to-primary-600",

      primaryBold:
        "bg-gradient-to-r from-primary-500 to-primary-400 dark:from-primary-600 dark:to-primary-500",

      // Secondary variations
      secondaryLight: isDark
        ? "bg-gradient-to-r from-secondary-800/30 to-secondary-700/30"
        : "bg-gradient-to-r from-secondary-100 to-secondary-50",

      secondaryMedium:
        "bg-gradient-to-r from-secondary-600 to-secondary-500 dark:from-secondary-700 dark:to-secondary-600",

      secondaryBold:
        "bg-gradient-to-r from-secondary-500 to-secondary-400 dark:from-secondary-600 dark:to-secondary-500",

      // Accent variations
      accentLight: isDark
        ? "bg-gradient-to-r from-accent-800/30 to-accent-700/30"
        : "bg-gradient-to-r from-accent-50 to-gray-50",

      accentMedium:
        "bg-gradient-to-r from-accent-600 to-accent-500 dark:from-accent-700 dark:to-accent-600",

      accentBold:
        "bg-gradient-to-r from-accent-500 to-accent-400 dark:from-accent-600 dark:to-accent-500",
    };

    // Combine all gradient sets
    return {
      ...baseGradients,
      ...extendedGradients,
      ...intensityVariations,

      // Animations from the original system
      animations: {
        goldenPulse: "animate-golden-pulse",
        goldenSpin: "animate-golden-spin",
        specialPulse: "animate-special-pulse",
        specialSpin: "animate-special-spin",
      },

      // Helper method from the original system
      getComponentGradient: (component, variant = "primary") => {
        const componentKey = component.toUpperCase();
        if (!GRADIENTS.COMPONENTS[componentKey]) {
          return "";
        }
        return GRADIENTS.COMPONENTS[componentKey][variant.toLowerCase()];
      },
    };
  }, [isDark]);

  return gradients;
}
